import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Container,
	Row,
	Col,
	Card,
	CardHeader,
	CardBody,
	Button,
} from 'reactstrap';
import BusinessInfoStep from './steps/BusinessInfoStep';
import OwnerInfoStep from './steps/OwnerInfoStep';
import BankInfoStep from './steps/BankInfoStep';
import RequestedAmountStep from './steps/RequestedAmountStep';
import apiService from 'service/apiService';
import merchantService from 'service/merchantService.ts';
import {
	validateBusinessInfo,
	validateOwnerInfo,
	validateOwnerAddress,
	validateOwnerContact,
	isBankInfoEmpty,
	validateRequestedAmount,
	validateTotalSubscribers,
	validateTotalMonthlyRevenue,
	validateBankInfo,
	validateOwnerAge,
} from './validation';
import InputsValidations from '../../../modules/InputsValidations.js';
import NotificationBanner from 'components/NotificationBanner';
import ConfirmationModal from 'components/ConfirmationModal';

const STEPS = {
	AMOUNT: 0,
	BUSINESS: 1,
	OWNER: 2,
	BANK: 3,
};

function Wizard({ MODE = 'APPLICATION', initialData = null }) {
	const navigate = useNavigate();
	const [currentStep, setCurrentStep] = useState(MODE === 'MERCHANT' ? STEPS.BUSINESS : STEPS.AMOUNT);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [requestedAmount, setRequestedAmount] = useState('');
	const [totalSubscribers, setTotalSubscribers] = useState('');
	const [totalMonthlyRevenue, setTotalMonthlyRevenue] = useState('');
	const [monitoringCenter, setMonitoringCenter] = useState('');
	const [monitoringCenterId, setMonitoringCenterId] = useState(null);

	const [businessInfo, setBusinessInfo] = useState({
		type: 'DEALER',
		legalName: '',
		dbaName: '',
		federalTaxId: '',
		industry: '',
		website: '',
		stateOfInc: '',
		businessStructure: '',
		dateEstablished: '',
		phoneNumber: '',
	});

	const [businessAddresses, setBusinessAddresses] = useState([]);
	const [businessAddressError, setBusinessAddressError] = useState('');

	const [owners, setOwners] = useState([]);
	const [bankInfo, setBankInfo] = useState({ banks: [], draft: {} });
	const [selectedMerchantId, setSelectedMerchantId] = useState(null);
	const [importLocked, setImportLocked] = useState(false);

	const [errors, setErrors] = useState({});
	const [successMessage, setSuccessMessage] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const stepHeaderRef = useRef(null);

	// Track if form has data to warn on navigation/reload
	const isDirty = useMemo(() => {
		if (MODE === 'APPLICATION' && (
			String(requestedAmount || '').trim() !== '' || 
			String(totalSubscribers || '').trim() !== '' || 
			String(totalMonthlyRevenue || '').trim() !== ''
		)) return true;
		const bizVals = Object.values(businessInfo || {}).some((v) => String(v || '').trim() !== '' && v !== 'DEALER');
		if (bizVals) return true;
		if ((businessAddresses || []).length > 0) return true;
		if ((owners || []).length > 0) return true;
		if ((bankInfo && Array.isArray(bankInfo.banks) && bankInfo.banks.length > 0)) return true;
		if (bankInfo && bankInfo.draft && Object.values(bankInfo.draft).some((v) => String(v || '').trim() !== '')) return true;
		return false;
	}, [MODE, requestedAmount, totalSubscribers, totalMonthlyRevenue, businessInfo, businessAddresses, owners, bankInfo]);

	// Removed beforeunload handler; reloads will not be intercepted.

	// Preload data when updating incomplete applications
	React.useEffect(() => {
		if (MODE !== 'APPLICATION_INCOMPLETE' || !initialData) return;
		try {
			const app = initialData;
			const payload = app.merchantPayload || {};
			const toIsoDate = (d) => {
				if (!d) return '';
				if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
				try {
					const dt = new Date(d);
					if (!isNaN(dt.getTime())) {
						const y = dt.getFullYear();
						const m = String(dt.getMonth()+1).padStart(2,'0');
						const da = String(dt.getDate()).padStart(2,'0');
						return `${y}-${m}-${da}`;
					}
				} catch(_) {}
				return '';
			};
			setRequestedAmount(String(app.amount || ''));
			setTotalSubscribers(String(app.totalSubscribers || ''));
			setTotalMonthlyRevenue(String(app.totalMonthlyRevenue || ''));
			setMonitoringCenter(String(app.monitoringCenter || ''));
			setMonitoringCenterId(app.monitoringCenterId || null);
			setSelectedMerchantId(payload.id || app.merchantId || null);
			setBusinessInfo({
				type: 'DEALER',
				legalName: payload.legalName || '',
				dbaName: payload.dbaName || '',
				federalTaxId: payload.federalTaxId || '',
				industry: payload.industry || '',
				website: payload.website || '',
				stateOfInc: payload.stateOfInc || '',
				businessStructure: payload.businessStructure || '',
				dateEstablished: toIsoDate(payload.dateEstablished),
				phoneNumber: payload.phoneNumber || '',
			});
			setBusinessAddresses((payload.addresses || []).map((a) => ({
				id: a.id,
				addressType: a.addressType || 'PHYSICAL',
				streetAddress: a.street1 || a.streetAddress || '',
				streetAddress2: a.street2 || a.streetAddress2 || '',
				city: a.city || '',
				state: a.state || '',
				zipCode: a.zipCode || '',
				country: a.country || 'USA',
				phoneNumber: a.phoneNumber || '',
			})));
			setOwners((payload.owners || []).map((o) => ({
				id: o.id,
				firstName: o.firstName,
				middleName: o.middleName,
				lastName: o.lastName,
				gender: o.gender,
				birthDate: o.birthDate,
				citizenship: o.citizenship,
				ownershipPercentage: o.ownershipPercentage,
				socialSecurityNumber: o.socialSecurityNumber,
				contacts: (o.contacs || o.contacts || []).map((c) => ({
					id: c.id,
					type: String(c.type || '').toUpperCase() === 'PHONE' ? 'Phone' : 'Email',
					value: c.value,
					isPrimary: c.isPrimary === true || c.isPrimary === 'true' || String(c.primary || '').toLowerCase() === 'yes',
				})),
				addresses: (o.addresses || []).map((a) => ({
					id: a.id,
					addressType: a.addressType || 'PHYSICAL',
					streetAddress: a.street1 || a.streetAddress || '',
					streetAddress2: a.street2 || a.streetAddress2 || '',
					city: a.city,
					state: a.state,
					zipCode: a.zipCode,
					country: a.country || 'USA',
					phoneNumber: a.phoneNumber || '',
				})),
			})));
		setBankInfo({
			banks: (payload.banks || []).map((b) => ({
				id: b.id,
				type: b.type,
				bankName: b.bankName,
				accountName: b.accountName,
				accountNumber: b.number,
				routingNumber: b.routingNumber,
				bankAddress: b.bankAddress ?? '',
				bankCity: b.bankCity ?? '',
				bankState: b.bankState ?? '',
				bankCountry: b.bankCountry ?? 'USA',
			})),
			draft: {},
		});
			// Start on amount step for APPLICATION_INCOMPLETE so user can update amount
			setCurrentStep(STEPS.AMOUNT);
		} catch(_) {}
	}, [MODE, initialData]);

	// Intercept in-app anchor navigations
	React.useEffect(() => {
		const onDocClick = (e) => {
			if (!isDirty) return;
			const anchor = e.target && e.target.closest && e.target.closest('a[href]');
			if (!anchor) return;
			const href = anchor.getAttribute('href');
			if (!href || href.startsWith('#')) return;
			try {
				const dest = new URL(href, window.location.href);
				if (dest.pathname === window.location.pathname && dest.search === window.location.search && dest.hash === window.location.hash) return;
			} catch (_) {
				return;
			}
			e.preventDefault();
			openModal({
				title: 'Confirmation',
				message: 'You have unsaved changes. Do you want to leave this page? Your changes will be lost.',
				icon: 'ni ni-fat-remove',
				confirmColor: 'danger',
				onConfirm: () => { window.location.assign(href); }
			});
		};
		document.addEventListener('click', onDocClick, true);
		return () => document.removeEventListener('click', onDocClick, true);
	}, [isDirty]);

	// Modal state for success/error feedback
	const [modalOpen, setModalOpen] = useState(false);
	const defaultModalConfig = {
		title: '',
		message: '',
		icon: 'ni ni-check-bold',
		confirmText: 'OK',
		cancelText: 'Close',
		confirmColor: 'primary',
		cancelColor: 'secondary',
		onConfirm: null,
		onCancel: null,
		showCancelButton: true,
	};
	const [modalConfig, setModalConfig] = useState(defaultModalConfig);

	const openModal = (config) => {
		// Reset to defaults every time to avoid leaking previous callbacks
		setModalConfig({ ...defaultModalConfig, ...config });
		setModalOpen(true);
	};

	const closeModal = () => {
		setModalOpen(false);
	};

	const resetWizardByMode = () => {
		setRequestedAmount('');
		setBusinessInfo({
			type: 'DEALER',
			legalName: '',
			dbaName: '',
			federalTaxId: '',
			industry: '',
			website: '',
			stateOfInc: '',
			businessStructure: '',
			dateEstablished: '',
			phoneNumber: '',
		});
		setBusinessAddresses([]);
		setOwners([]);
		setBankInfo({ banks: [], draft: {} });
		setErrors({});
		setBusinessAddressError('');
		setSelectedMerchantId(null);
		setImportLocked(false);
		setCurrentStep(MODE === 'MERCHANT' ? STEPS.BUSINESS : STEPS.AMOUNT);
	};

	// Import merchant data into the wizard (APPLICATION mode) from a duplicate selection
	const handleSelectDuplicateMerchant = async (merchantSummary) => {
		if (MODE !== 'APPLICATION' || !merchantSummary || !merchantSummary.id) return;
		openModal({
			title: 'Use existing merchant?',
			message: 'Do you want to fill the form with this merchant data? This will replace the current values.',
			icon: 'ni ni-check-bold',
			confirmColor: 'primary',
			onConfirm: async () => {
				try {
					const token = await apiService.getToken();
					const { data } = await merchantService.getMerchantById(token, merchantSummary.id);
					const m = data || {};
					const base = m.merchant || m;
					setSelectedMerchantId(merchantSummary.id);
					setImportLocked(true);
					const normalizeDateForInput = (d) => {
						if (!d) return '';
						try {
							if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d; // already ISO
							if (/^\d{2}[\/\-]\d{2}[\/\-]\d{4}$/.test(d)) {
								const parts = d.includes('/') ? d.split('/') : d.split('-');
								return `${parts[2]}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`;
							}
							const dt = new Date(d);
							if (!isNaN(dt.getTime())) {
								const y = dt.getFullYear();
								const m = String(dt.getMonth()+1).padStart(2,'0');
								const da = String(dt.getDate()).padStart(2,'0');
								return `${y}-${m}-${da}`;
							}
						} catch(_) {}
						return '';
					};
					// Business info
					setBusinessInfo({
						type: 'DEALER',
						legalName: base.legalName || '',
						dbaName: base.dbaName || '',
						federalTaxId: base.federalTaxId || '',
						industry: base.industry || '',
						website: base.website || '',
						stateOfInc: base.stateOfInc || '',
						businessStructure: base.businessStructure || '',
						dateEstablished: normalizeDateForInput(base.dateEstablished),
						phoneNumber: base.phoneNumber || ''
					});
					// Addresses
					setBusinessAddresses((m.addresses || []).map((addr) => ({
						id: addr.id,
						addressType: addr.addressType || 'PHYSICAL',
						streetAddress: addr.street1 || addr.streetAddress || '',
						streetAddress2: addr.street2 || addr.streetAddress2 || addr.unit || '',
						city: addr.city || '',
						state: addr.state || '',
						zipCode: addr.zipCode || '',
						country: addr.country || 'USA',
						phoneNumber: addr.phoneNumber || ''
					})));
					// Owners
					setOwners((m.owners || []).map((o) => {
						const ow = o.owner || o;
						return {
							firstName: ow.firstName,
							middleName: ow.middleName,
							lastName: ow.lastName,
							gender: ow.gender,
							birthDate: ow.birthDate,
							citizenship: ow.citizenship,
							ownershipPercentage: ow.ownershipPercentage,
							socialSecurityNumber: ow.socialSecurityNumber,
							addresses: (o.addresses || ow.addresses || []).map((a) => ({
								id: a.id,
								addressType: a.addressType || 'PHYSICAL',
								streetAddress: a.street1 || a.streetAddress || '',
								streetAddress2: a.street2 || a.streetAddress2 || '',
								city: a.city,
								state: a.state,
								zipCode: a.zipCode,
								country: a.country || 'USA',
								phoneNumber: a.phoneNumber || ''
							})),
							contacts: (o.contacts || []).map((c) => ({
								id: c.id,
								type: String(c.type || '').toUpperCase() === 'PHONE' ? 'Phone' : 'Email',
								value: c.value,
								isPrimary: typeof c.isPrimary === 'boolean' ? c.isPrimary : (String(c.primary || '').toLowerCase() === 'yes' || String(c.isPrimary || '').toLowerCase() === 'true')
							}))
						};
					}));
					// Banks -> bankInfo.banks
					const banks = (m.banks || []).map((b) => ({
						id: b.id,
						type: b.type,
						bankName: b.bankName,
						accountName: b.accountName,
						accountNumber: b.number,
						routingNumber: b.routingNumber,
						bankAddress: b.bankAddress ?? '',
						bankCity: b.bankCity ?? '',
						bankState: b.bankState ?? '',
						bankCountry: b.bankCountry ?? null
					}));
					setBankInfo({ banks, draft: {} });
					setErrors({});
					setBusinessAddressError('');
					setCurrentStep(STEPS.BUSINESS);
				} catch (e) {
					openModal({
						title: 'Error',
						message: 'Unable to import merchant data. Please try again.',
						icon: 'ni ni-fat-remove',
						confirmColor: 'danger',
					});
				}
			}
		});
	};

	// Function to handle business info input changes with merchant validations
	const handleInputChange = (e) => {
		const { name, value } = e.target;
		let processedValue = value;
		
		// In APPLICATION mode, type is fixed to DEALER. In MERCHANT mode it's editable.
		if (name === 'type' && MODE === 'APPLICATION') {
			return;
		}
		
		// Si el usuario está editando datos clave del merchant en APPLICATION, desasociar merchant seleccionado previo
		if (MODE === 'APPLICATION' && ['legalName','dbaName','federalTaxId','industry','website','stateOfInc','businessStructure','dateEstablished','phoneNumber'].includes(name)) {
			if (selectedMerchantId) setSelectedMerchantId(null);
			if (importLocked) setImportLocked(false);
		}
		
		// Clear error message if user is fixing the requested amount
		if (name === 'requestedAmount' && errorMessage && errorMessage.includes('requested amount')) {
			clearErrorMessages();
		}
		
		// Check character limits for Legal Name and DBA Name
		if ((name === 'legalName' || name === 'dbaName') && value.length > 255) {
			// Don't update the value if it exceeds the limit
			return;
		}
		
		// Apply formatting for Federal Tax ID
		if (name === 'federalTaxId') {
			processedValue = InputsValidations.formatFederalTaxId(value, businessInfo.federalTaxId);
		}
		
		setBusinessInfo(prev => ({
			...prev,
			[name]: processedValue
		}));
		
		// Validate field in real-time if it has a validation function
		if (InputsValidations.businessInformationValidations[name]) {
			const validation = InputsValidations.businessInformationValidations[name];
			const result = validation(processedValue);
			
			if (!result.isValid) {
				setErrors(prev => ({
					...prev,
					[name]: result.message
				}));
			} else {
				// Clear error for this field if validation passes
				setErrors(prev => ({
					...prev,
					[name]: undefined
				}));
			}
		} else {
			// Clear error for this field when user starts typing (for fields without specific validation)
			if (errors[name]) {
				setErrors(prev => ({
					...prev,
					[name]: undefined
				}));
			}
		}
	};

	// Function to handle bank info changes with validation
	const handleBankInfoChange = (newBankInfo) => {
		setBankInfo(newBankInfo);
		
		// Clear bank errors when user makes changes
		if (errors.bank) {
			setErrors(prev => {
				const newErrors = { ...prev };
				delete newErrors.bank;
				return newErrors;
			});
		}
		// Clear error message if user is fixing validation issues
		if (errorMessage && errorMessage.includes('validation errors')) {
			clearErrorMessages();
		}
	};

	const clearSuccess = () => {
		setSuccessMessage(null);
	};

	const clearError = () => {
		setErrorMessage(null);
	};

	// Function to clear error messages when user starts fixing issues
	const clearErrorMessages = () => {
		setErrorMessage(null);
		setBusinessAddressError('');
	};

	// Function to process API errors and show user-friendly messages
	const processApiError = (error) => {
		// Prefer server-provided message when available
		const serverMessage = error?.response?.data?.message || error?.data?.message || error?.message;
		if (serverMessage) return serverMessage;
		return 'An unexpected error occurred. Please try again.';
	};

	const stepValid = useMemo(() => {
		if (currentStep === STEPS.AMOUNT) {
			if (MODE === 'APPLICATION' || MODE === 'APPLICATION_INCOMPLETE') {
				return validateRequestedAmount(requestedAmount) === null && 
					   validateTotalSubscribers(totalSubscribers) === null && 
					   validateTotalMonthlyRevenue(totalMonthlyRevenue) === null;
			}
			return true;
		}
		if (currentStep === STEPS.BUSINESS) {
			const okForm = Object.keys(validateBusinessInfo(businessInfo)).length === 0;
			return okForm && businessAddresses.length > 0;
		}
		if (currentStep === STEPS.OWNER) {
			// Check if we have at least one owner
			return owners.length > 0;
		}
		if (currentStep === STEPS.BANK) {
			// Bank step is completely optional - always valid
			return true;
		}
		return true;
	}, [currentStep, requestedAmount, totalSubscribers, totalMonthlyRevenue, businessInfo, businessAddresses.length, owners.length]);

	const goNext = async () => {
		if (currentStep === STEPS.AMOUNT) {
			const amountErr = validateRequestedAmount(requestedAmount);
			const subscribersErr = validateTotalSubscribers(totalSubscribers);
			const revenueErr = validateTotalMonthlyRevenue(totalMonthlyRevenue);
			
			const errors = {};
			if (amountErr) errors.requestedAmount = amountErr;
			if (subscribersErr) errors.totalSubscribers = subscribersErr;
			if (revenueErr) errors.totalMonthlyRevenue = revenueErr;
			
			setErrors(errors);
			if (Object.keys(errors).length > 0) { focusFirstError(); return; }
			setCurrentStep(STEPS.BUSINESS);
			return;
		}
		if (currentStep === STEPS.BUSINESS) {
			const v = validateBusinessInfo(businessInfo);
			if (businessAddresses.length === 0) {
				setBusinessAddressError('At least one address is required');
			}
			setErrors(v);
			if (Object.keys(v).length > 0 || businessAddresses.length === 0) {
				focusFirstError();
				return;
			}

			// validateUnique solo para MERCHANT
			if (MODE === 'MERCHANT') {
				try {
					const token = await apiService.getToken();
					const payload = {
						legalName: businessInfo.legalName || '',
						dbaName: businessInfo.dbaName || '',
						federalTaxId: businessInfo.federalTaxId || ''
					};
					const { data } = await merchantService.validateUnique(token, payload);
					if (data && data.isUnique === false) {
						openModal({
							title: 'Duplicate detected',
							message: `Field ${data.conflictingColumn} duplicates value: ${data.conflictingValue}`,
							icon: 'ni ni-fat-remove',
							confirmColor: 'danger',
						});
						return;
					}
				} catch (e) {
					openModal({
						title: 'Validation Error',
						message: 'Unable to validate uniqueness at this time. Please try again.',
						icon: 'ni ni-fat-remove',
						confirmColor: 'danger',
					});
					return;
				}
			}

			setCurrentStep(STEPS.OWNER);
			return;
		}
		if (currentStep === STEPS.OWNER) {
			// Check if we have at least one owner
			if (owners.length === 0) {
				setErrorMessage('Please add at least one owner before proceeding.');
				setTimeout(() => setErrorMessage(null), 8000);
				focusFirstError();
				return;
			}

			// Validate age for all owners (must be at least 18 years old)
			const ownersWithInvalidAge = owners.filter(o => {
				const ageError = validateOwnerAge(o.birthDate);
				return ageError !== null;
			});

			if (ownersWithInvalidAge.length > 0) {
				const ownerNames = ownersWithInvalidAge.map(o => `${o.firstName} ${o.lastName}`).join(', ');
				openModal({
					title: 'Validation Error',
					message: `The following owner(s) must be at least 18 years old: ${ownerNames}`,
					icon: 'ni ni-fat-remove',
					confirmColor: 'danger',
				});
				return;
			}

			const totalOwnership = owners.reduce((sum, o) => sum + (parseFloat(o.ownershipPercentage) || 0), 0);
			if (totalOwnership !== 100) {
			  openModal({
				title: 'Validation Error',
				message: 'The sum of ownership percentages across all owners must equal 100%.',
				icon: 'ni ni-fat-remove',
				confirmColor: 'danger',
			  });
			  return;
			}

			setCurrentStep(STEPS.BANK);
			return;
		}
		if (currentStep === STEPS.BANK) {
			// Bank step is completely optional - no validation needed
			return;
		}
	};

	const goBack = () => {
		// Handle first step per mode
		if (MODE === 'APPLICATION') {
			if (currentStep === STEPS.AMOUNT) {
				navigate('/applications');
				return;
			}
		} else if (MODE === 'MERCHANT') {
			if (currentStep === STEPS.BUSINESS) {
				navigate('/merchants');
				return;
			}
		} else if (MODE === 'APPLICATION_INCOMPLETE') {
			if (currentStep === STEPS.AMOUNT) {
				navigate('/applications');
				return;
			}
		}
		// Otherwise, go to previous step
		if (currentStep > STEPS.AMOUNT) setCurrentStep(currentStep - 1);
	};

	const focusFirstError = () => {
		setTimeout(() => {
			const el = document.querySelector('.is-invalid');
			if (el && el.scrollIntoView) {
				el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				if (el.focus) el.focus();
			}
			if (stepHeaderRef.current) stepHeaderRef.current.focus();
		}, 0);
	};

	const handleSubmit = async () => {
		// Validate amount (only for application), business and owner before submit
		const aErr = MODE === 'APPLICATION' ? validateRequestedAmount(requestedAmount) : null;
		const sErr = MODE === 'APPLICATION' ? validateTotalSubscribers(totalSubscribers) : null;
		const rErr = MODE === 'APPLICATION' ? validateTotalMonthlyRevenue(totalMonthlyRevenue) : null;
		const bErr = MODE === 'APPLICATION_INCOMPLETE' ? {} : validateBusinessInfo(businessInfo);
		const bankErr = MODE === 'APPLICATION_INCOMPLETE' ? {} : validateBankInfo(bankInfo);
		const merged = { 
			...bErr, 
			...(aErr ? { requestedAmount: aErr } : {}), 
			...(sErr ? { totalSubscribers: sErr } : {}), 
			...(rErr ? { totalMonthlyRevenue: rErr } : {}), 
			...(Object.keys(bankErr).length > 0 ? { bank: bankErr } : {}) 
		};
		setErrors(merged);
		
		// Validate business addresses (skip for APPLICATION_INCOMPLETE)
		if (MODE !== 'APPLICATION_INCOMPLETE') {
			if (businessAddresses.length === 0) {
				setBusinessAddressError('At least one address is required');
				openModal({
					title: 'Validation Error',
					message: 'Please add at least one business address before submitting.',
					icon: 'ni ni-fat-remove',
					confirmColor: 'danger',
				});
				focusFirstError();
				return;
			}
		}
		
		// Validate owners (skip for APPLICATION_INCOMPLETE)
		if (MODE !== 'APPLICATION_INCOMPLETE') {
			if (owners.length === 0) {
				openModal({
					title: 'Validation Error',
					message: 'Please add at least one owner before submitting.',
					icon: 'ni ni-fat-remove',
					confirmColor: 'danger',
				});
				focusFirstError();
				return;
			}
		}
		
		// Validate requested amount (only for APPLICATION)
		if (MODE === 'APPLICATION') {
			if (parseFloat(requestedAmount) < 1) {
				setErrors(prev => ({ ...prev, requestedAmount: 'Requested amount must be at least 1' }));
				openModal({
					title: 'Validation Error',
					message: 'Please enter a valid requested amount (minimum $1) before submitting.',
					icon: 'ni ni-fat-remove',
					confirmColor: 'danger',
				});
				focusFirstError();
				return;
			}
		}
		
		if (Object.keys(merged).length > 0) {
			openModal({
				title: 'Validation Error',
				message: 'Please correct the validation errors before submitting.',
				icon: 'ni ni-fat-remove',
				confirmColor: 'danger',
			});
			focusFirstError();
			return;
		}

		// Extra validations only for APPLICATION_INCOMPLETE -> quitadas (se permite enviar tal cual)

		if (MODE === 'APPLICATION') {
			// Build payload for new Applications endpoint
			const toIsoDate = (d) => {
				if (!d) return undefined;
				if (typeof d === 'string' && d.includes('-')) return d;
				if (typeof d === 'string' && d.includes('/')) {
					const p = d.split('/');
					if (p.length === 3) return `${p[2]}-${p[0]}-${p[1]}`;
				}
				try {
					const date = new Date(d);
					if (!isNaN(date.getTime())) {
						const yyyy = date.getFullYear();
						const mm = String(date.getMonth() + 1).padStart(2, '0');
						const dd = String(date.getDate()).padStart(2, '0');
						return `${yyyy}-${mm}-${dd}`;
					}
				} catch (_) { /* noop */ }
				return undefined;
			};

			const addressDtos = businessAddresses.map((addr) => ({
				...(addr && addr.id ? { id: addr.id } : {}),
				addressType: addr.addressType || 'PHYSICAL',
				street1: addr.streetAddress,
				street2: addr.streetAddress2 || addr.unit || null,
				city: addr.city,
				state: addr.state,
				zipCode: addr.zipCode,
				country: addr.country || 'USA',
				phoneNumber: addr.phoneNumber || null,
			}));

			const ownerDtos = owners.map((o) => ({
				...(o && o.id ? { id: o.id } : {}),
				firstName: o.firstName,
				middleName: o.middleName,
				lastName: o.lastName,
				gender: (o.gender || 'OTHER').toString().toUpperCase(),
				birthDate: o.birthDate,
				citizenship: o.citizenship,
				ownershipPercentage: parseFloat(o.ownershipPercentage) || 0,
				socialSecurityNumber: o.socialSecurityNumber,
				contacts: (o.contacts || []).map((c, idx) => ({
					...(c && c.id ? { id: c.id } : {}),
					type: String(c.type || '').toUpperCase() === 'PHONE' ? 'PHONE' : 'EMAIL',
					value: c.value,
					isPrimary: typeof c.isPrimary === 'boolean' ? c.isPrimary : (String(c.primary || '').toLowerCase() === 'yes' || String(c.primary || '').toLowerCase() === 'true'),
				})),
				addresses: (o.addresses || []).map((a) => ({
					...(a && a.id ? { id: a.id } : {}),
					addressType: a.addressType || 'PHYSICAL',
					street1: a.streetAddress || a.street1,
					street2: a.streetAddress2 || a.street2 || null,
					city: a.city,
					state: a.state,
					zipCode: a.zipCode,
					country: a.country || 'USA',
					phoneNumber: a.phoneNumber || null,
				})),
			}));

			const banks = (() => {
				if (isBankInfoEmpty(bankInfo)) return [];
				const all = [...(bankInfo.banks || [])];
				const draft = bankInfo.draft || {};
				const hasDraft = Object.values(draft).some((v) => String(v || '').trim() !== '');
				if (hasDraft) all.push(draft);
				return all.map((b) => ({
					...(b && b.id ? { id: b.id } : {}),
					type: b.type,
					bankName: b.bankName,
					accountName: b.accountName,
					number: b.number || b.accountNumber,
					routingNumber: b.routingNumber,
					bankAddress: b.bankAddress ?? '',
					bankCity: b.bankCity ?? '',
					bankState: b.bankState ?? '',
					bankCountry: b.bankCountry ?? 'USA',
				}));
			})();

			const payload = {
				amount: parseFloat(requestedAmount),
				totalSubscribers: parseInt(totalSubscribers),
				totalMonthlyRevenue: parseFloat(totalMonthlyRevenue),
				monitoringCenter: monitoringCenter,
				monitoringCenterId: monitoringCenterId,
				merchantPayload: {
					id: selectedMerchantId || undefined,
					type: businessInfo.type || 'DEALER',
					legalName: businessInfo.legalName,
					dbaName: businessInfo.dbaName,
					federalTaxId: businessInfo.federalTaxId,
					stateOfInc: businessInfo.stateOfInc,
					industry: businessInfo.industry,
					businessStructure: businessInfo.businessStructure,
					website: businessInfo.website || undefined,
					dateEstablished: toIsoDate(businessInfo.dateEstablished),
					phoneNumber: businessInfo.phoneNumber || undefined,
					addresses: addressDtos,
					owners: ownerDtos,
					banks,
				},
				merchantId: selectedMerchantId || null,
			};

			setIsSubmitting(true);
			try {
				await apiService.createApplication(payload);
				openModal({
					title: 'Success',
					message: 'Application created successfully!',
					icon: 'ni ni-check-bold',
					confirmColor: 'success',
					confirmText: 'OK',
					showCancelButton: false,
					onConfirm: () => {
						resetWizardByMode();
					},
					onCancel: () => {
						resetWizardByMode();
					}
				});
			} catch (error) {
				const message = processApiError(error);
				openModal({
					title: 'Server Error',
					message,
					icon: 'ni ni-fat-remove',
					confirmColor: 'danger',
				});
			} finally {
				setIsSubmitting(false);
			}
			return;
		}

		// APPLICATION_INCOMPLETE mode -> update existing application
		if (MODE === 'APPLICATION_INCOMPLETE') {
			const toIsoDate = (d) => {
				if (!d) return undefined;
				if (typeof d === 'string' && d.includes('-')) return d;
				if (typeof d === 'string' && d.includes('/')) {
					const p = d.split('/');
					if (p.length === 3) return `${p[2]}-${p[0]}-${p[1]}`;
				}
				try {
					const date = new Date(d);
					if (!isNaN(date.getTime())) {
						const yyyy = date.getFullYear();
						const mm = String(date.getMonth() + 1).padStart(2, '0');
						const dd = String(date.getDate()).padStart(2, '0');
						return `${yyyy}-${mm}-${dd}`;
					}
				} catch (_) { /* noop */ }
				return undefined;
			};

			const addressDtos = businessAddresses.map((addr) => ({
				...(addr && addr.id ? { id: addr.id } : {}),
				addressType: addr.addressType || 'PHYSICAL',
				street1: addr.streetAddress,
				street2: addr.streetAddress2 || addr.unit || null,
				city: addr.city,
				state: addr.state,
				zipCode: addr.zipCode,
				country: addr.country || 'USA',
				phoneNumber: addr.phoneNumber || null,
			}));

			const ownerDtos = owners.map((o) => ({
				...(o && o.id ? { id: o.id } : {}),
				firstName: o.firstName,
				middleName: o.middleName,
				lastName: o.lastName,
				gender: (o.gender || 'OTHER').toString().toUpperCase(),
				birthDate: o.birthDate,
				citizenship: o.citizenship,
				ownershipPercentage: parseFloat(o.ownershipPercentage) || 0,
				socialSecurityNumber: o.socialSecurityNumber,
				contacts: (o.contacts || []).map((c) => ({
					...(c && c.id ? { id: c.id } : {}),
					type: String(c.type || '').toUpperCase() === 'PHONE' ? 'PHONE' : 'EMAIL',
					value: c.value,
					isPrimary: typeof c.isPrimary === 'boolean' ? c.isPrimary : (String(c.primary || '').toLowerCase() === 'yes' || String(c.primary || '').toLowerCase() === 'true'),
				})),
				addresses: (o.addresses || []).map((a) => ({
					...(a && a.id ? { id: a.id } : {}),
					addressType: a.addressType || 'PHYSICAL',
					street1: a.streetAddress || a.street1,
					street2: a.streetAddress2 || a.street2 || null,
					city: a.city,
					state: a.state,
					zipCode: a.zipCode,
					country: a.country || 'USA',
					phoneNumber: a.phoneNumber || null,
				})),
			}));

			const banks = (() => {
				if (isBankInfoEmpty(bankInfo)) return [];
				const all = [...(bankInfo.banks || [])];
				const draft = bankInfo.draft || {};
				const hasDraft = Object.values(draft).some((v) => String(v || '').trim() !== '');
				if (hasDraft) all.push(draft);
				return all.map((b) => ({
					...(b && b.id ? { id: b.id } : {}),
					type: b.type,
					bankName: b.bankName,
					accountName: b.accountName,
					number: b.number || b.accountNumber,
					routingNumber: b.routingNumber,
					bankAddress: b.bankAddress ?? '',
					bankCity: b.bankCity ?? '',
					bankState: b.bankState ?? '',
					bankCountry: b.bankCountry ?? 'USA',
				}));
			})();

			const payload = {
				amount: parseFloat(requestedAmount),
				totalSubscribers: parseInt(totalSubscribers),
				totalMonthlyRevenue: parseFloat(totalMonthlyRevenue),
				monitoringCenter: monitoringCenter,
				monitoringCenterId: monitoringCenterId,
				merchantPayload: {
					id: selectedMerchantId || undefined,
					type: businessInfo.type || 'DEALER',
					legalName: businessInfo.legalName,
					dbaName: businessInfo.dbaName,
					federalTaxId: businessInfo.federalTaxId,
					stateOfInc: businessInfo.stateOfInc,
					industry: businessInfo.industry,
					businessStructure: businessInfo.businessStructure,
					website: businessInfo.website || undefined,
					dateEstablished: toIsoDate(businessInfo.dateEstablished),
					phoneNumber: businessInfo.phoneNumber || undefined,
					addresses: addressDtos,
					owners: ownerDtos,
					banks,
				},
				merchantId: selectedMerchantId || null,
			};

			setIsSubmitting(true);
			try {
				await apiService.updateApplication(initialData?.id, payload);
				openModal({
					title: 'Success',
					message: 'Application updated successfully!',
					icon: 'ni ni-check-bold',
					confirmColor: 'success',
					confirmText: 'OK',
					showCancelButton: false,
					onConfirm: () => {
						navigate('/applications');
					},
					onCancel: () => {
						navigate('/applications');
					}
				});
			} catch (error) {
				const message = processApiError(error);
				openModal({
					title: 'Server Error',
					message,
					icon: 'ni ni-fat-remove',
					confirmColor: 'danger',
				});
			} finally {
				setIsSubmitting(false);
			}
			return;
		}

		// MERCHANT mode -> call merchant endpoint (new payload schema)
		const addressDtos = businessAddresses.map(addr => ({
			addressType: addr.addressType || 'PHYSICAL',
			street1: addr.streetAddress,
			street2: addr.streetAddress2 || addr.unit || null,
			city: addr.city,
			state: addr.state,
			zipCode: addr.zipCode,
			country: addr.country || 'USA',
			phoneNumber: addr.phoneNumber || null
		}));

		const ownerDtos = owners.map(o => ({
			firstName: o.firstName,
			middleName: o.middleName,
			lastName: o.lastName,
			gender: o.gender?.toUpperCase?.() || 'OTHER',
			birthDate: o.birthDate,
			citizenship: o.citizenship,
			ownershipPercentage: parseFloat(o.ownershipPercentage) || 0,
			socialSecurityNumber: o.socialSecurityNumber,
			contacts: (o.contacts || []).map((contact) => {
				const normalizedType = String(contact.type || '').trim().toUpperCase();
				const isPrimary = typeof contact.isPrimary === 'boolean' ? contact.isPrimary : (String(contact.primary || '').toLowerCase() === 'yes' || String(contact.primary || '').toLowerCase() === 'true');
				return {
					type: normalizedType === 'PHONE' ? 'PHONE' : 'EMAIL',
					value: contact.value,
					isPrimary,
				};
			}),
			addresses: (o.addresses || []).map(a => ({
				addressType: a.addressType || 'PHYSICAL',
				street1: a.streetAddress || a.street1,
				street2: a.streetAddress2 || a.street2 || null,
				city: a.city,
				state: a.state,
				zipCode: a.zipCode,
				country: a.country || 'USA',
				phoneNumber: a.phoneNumber || null
			}))
		}));

		const banks = (() => {
			const all = [...(bankInfo.banks || [])];
			const draft = bankInfo.draft || {};
			const hasDraft = Object.values(draft).some((v) => String(v || '').trim() !== '');
			if (hasDraft) all.push(draft);
			return all.map(b => ({
				type: b.type,
				bankName: b.bankName,
				accountName: b.accountName,
				number: b.number || b.accountNumber,
				routingNumber: b.routingNumber,
				bankAddress: b.bankAddress ?? '',
				bankCity: b.bankCity ?? '',
				bankState: b.bankState ?? '',
				bankCountry: b.bankCountry ?? 'USA'
			}));
		})();

		const merchantPayload = {
			legalName: businessInfo.legalName,
			dbaName: businessInfo.dbaName || undefined,
			federalTaxId: businessInfo.federalTaxId,
			stateOfInc: businessInfo.stateOfInc,
			industry: businessInfo.industry,
			businessStructure: businessInfo.businessStructure,
			website: businessInfo.website || undefined,
			dateEstablished: businessInfo.dateEstablished || undefined,
			phoneNumber: businessInfo.phoneNumber || undefined,
			type: (businessInfo.type || 'DEALER'),
			addresses: addressDtos,
			owners: ownerDtos,
			banks
		};

		setIsSubmitting(true);
		try {
			const token = await apiService.getToken();
			await merchantService.createMerchantWithDetails(token, merchantPayload);
			openModal({
				title: 'Success',
				message: 'Merchant created successfully!',
				icon: 'ni ni-check-bold',
				confirmColor: 'success',
				confirmText: 'OK',
				showCancelButton: false,
				onConfirm: () => {
					navigate('/merchants');
				},
				onCancel: () => {
					navigate('/merchants');
				}
			});
		} catch (error) {
			const message = processApiError(error);
			openModal({
				title: 'Server Error',
				message,
				icon: 'ni ni-fat-remove',
				confirmColor: 'danger',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const renderProgress = () => {
		const steps = (
			(MODE === 'APPLICATION' || MODE === 'APPLICATION_INCOMPLETE')
				? [
					{ key: STEPS.AMOUNT, label: 'Business Metrics', icon: 'ni ni-money-coins' },
					{ key: STEPS.BUSINESS, label: 'Business Information', icon: 'ni ni-briefcase-24' },
					{ key: STEPS.OWNER, label: 'Owner Information', icon: 'ni ni-single-02' },
					{ key: STEPS.BANK, label: 'Bank Information (optional)', icon: 'ni ni-credit-card' },
				]
				: [
					{ key: STEPS.BUSINESS, label: 'Business Information', icon: 'ni ni-briefcase-24' },
					{ key: STEPS.OWNER, label: 'Owner Information', icon: 'ni ni-single-02' },
					{ key: STEPS.BANK, label: 'Bank Information (optional)', icon: 'ni ni-credit-card' },
				]
		);
		return (
			<div className="progress-container" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
				<div className="d-flex align-items-center flex-wrap" role="tablist" aria-label="Application steps">
					{steps.map((s, idx) => (
						<div key={s.key} className={`d-flex align-items-center flex-shrink-0 ${idx > 0 ? 'ml-2 ml-md-3' : ''}`}>
							<div
								className={`rounded-circle d-flex align-items-center justify-content-center ${currentStep >= s.key ? 'bg-primary text-white' : 'bg-secondary text-muted'}`}
								style={{ width: 32, height: 32, minWidth: 32 }}
								aria-current={currentStep === s.key ? 'step' : undefined}
								title={s.label}
							>
								<i className={`${s.icon}`} style={{ fontSize: '0.875rem' }} />
							</div>
							<span className={`ml-1 ml-md-2 ${currentStep === s.key ? 'font-weight-bold' : 'text-muted'}`} style={{ fontSize: '0.875rem' }}>
								{s.label}
							</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<>
			<style>
				{`
					.progress-container {
						scrollbar-width: thin;
						scrollbar-color: #dee2e6 transparent;
					}
					.progress-container::-webkit-scrollbar { height: 4px; }
					.progress-container::-webkit-scrollbar-track { background: transparent; }
					.progress-container::-webkit-scrollbar-thumb { background-color: #dee2e6; border-radius: 2px; }
					@media (max-width: 767.98px) { .progress-container { margin: 0 -1rem; padding: 0 1rem; } }
					.wizard-dup-row:hover { background-color: #f0f6ff !important; }
				`}
			</style>
			<Card className="shadow">
				<CardHeader className="border-0 mb-1 d-flex flex-column">
					<h1 className="m-0 p-0 mb-2" tabIndex={-1} ref={stepHeaderRef}>
						{MODE === 'MERCHANT' ? 'New Merchant' : (MODE === 'APPLICATION_INCOMPLETE' ? 'Update Application' : 'New Application')}
					</h1>
					<div className="w-100">
						{renderProgress()}
					</div>
				</CardHeader>
				<CardBody className='pt-1'>
					{/* Banners deprecated in favor of modal feedback */}
					{/* Notification banners kept for backward compatibility but not used */}
					{/* <NotificationBanner type="error" message={errorMessage} onClose={clearError} show={!!errorMessage} /> */}
					{/* <NotificationBanner type="success" message={successMessage} onClose={clearSuccess} show={!!successMessage} /> */}
					{(MODE === 'APPLICATION' || MODE === 'APPLICATION_INCOMPLETE') && currentStep === STEPS.AMOUNT && (
						<RequestedAmountStep
							value={requestedAmount}
							onChange={setRequestedAmount}
							errors={errors}
							totalSubscribers={totalSubscribers}
							onTotalSubscribersChange={setTotalSubscribers}
							totalMonthlyRevenue={totalMonthlyRevenue}
							onTotalMonthlyRevenueChange={setTotalMonthlyRevenue}
							monitoringCenter={monitoringCenter}
							onMonitoringCenterChange={(e) => setMonitoringCenter(e.target.value)}
							monitoringCenterId={monitoringCenterId}
							onMonitoringCenterIdChange={setMonitoringCenterId}
							mode={MODE}
						/>
					)}
					{currentStep === STEPS.BUSINESS && (
						<BusinessInfoStep
							value={businessInfo}
							onChange={handleInputChange}
							errors={errors}
							addresses={businessAddresses}
							onAddressesChange={(list) => { 
								setBusinessAddresses(list); 
								if (list.length > 0) {
									setBusinessAddressError('');
									// Clear error message if user is fixing the issue
									if (errorMessage && errorMessage.includes('business address')) {
										clearErrorMessages();
									}
								}
							}}
							addressError={businessAddressError}
							onClearAddressError={() => setBusinessAddressError('')}
							mode={MODE}
							onDuplicateSelect={handleSelectDuplicateMerchant}
							locked={MODE === 'APPLICATION' && importLocked}
						/>
					)}
				{currentStep === STEPS.OWNER && (
					<OwnerInfoStep
						owners={owners}
						onOwnersChange={setOwners}
						errors={errors}
						locked={MODE === 'APPLICATION' && importLocked}
						mode={MODE}
					/>
				)}
					{currentStep === STEPS.BANK && (
						<BankInfoStep
							value={bankInfo}
							onChange={handleBankInfoChange}
							errors={errors.bank || {}}
							locked={MODE === 'APPLICATION' && importLocked}
						/>
					)}

					<Row className="mt-0">
						<Col className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center">
							<Button 
								color="secondary" 
								onClick={goBack}
								className="mb-2 mb-sm-0"
								size="sm"
							>
								<i className="ni ni-bold-left mr-1 mr-sm-2" /> 
								<span className="d-none d-sm-inline">Back</span>
							</Button>
							<div>
								{currentStep < STEPS.BANK && (
									<Button 
										color="primary" 
										onClick={goNext} 
										disabled={!stepValid}
										size="sm"
										className="w-100 w-sm-auto"
									>
										<span className="d-none d-sm-inline">Next</span> 
										<i className="ni ni-bold-right ml-1 ml-sm-2" />
									</Button>
								)}
								{currentStep === STEPS.BANK && (
									<Button 
										color="primary" 
										onClick={handleSubmit} 
										disabled={isSubmitting}
										size="sm"
										className="w-100 w-sm-auto"
									>
										{isSubmitting ? (
											<>
												<span className="spinner-border spinner-border-sm mr-1 mr-sm-2" role="status" aria-hidden="true"></span>
												<span className="d-none d-sm-inline">Submitting...</span>
											</>
										) : (
											<>
												<span className="d-none d-sm-inline">{MODE === 'APPLICATION_INCOMPLETE' ? 'Update & Finish' : 'Finish & Submit'}</span>
												<span className="d-inline d-sm-none">{MODE === 'APPLICATION_INCOMPLETE' ? 'Update' : 'Submit'}</span>
											</>
										)}
									</Button>
								)}
						</div>
					</Col>
					</Row>

					<ConfirmationModal
						isOpen={modalOpen}
						toggle={closeModal}
						title={modalConfig.title}
						message={modalConfig.message}
						onConfirm={() => {
							if (typeof modalConfig.onConfirm === 'function') modalConfig.onConfirm();
							closeModal();
						}}
						onCancel={() => {
							if (typeof modalConfig.onCancel === 'function') modalConfig.onCancel();
							closeModal();
						}}
						confirmText={modalConfig.confirmText}
						cancelText={modalConfig.cancelText}
						confirmColor={modalConfig.confirmColor}
						cancelColor={modalConfig.cancelColor}
						icon={modalConfig.icon}
						showCancelButton={modalConfig.showCancelButton !== false}
					/>
				</CardBody>
			</Card>
		</>
	);
}

export default Wizard;
